import { captureException } from '@sentry/browser';
import { logger } from 'lib/logger';
import { SubscriptionBuyTrialAPICall } from 'reactApp/api/billing/subscription/SubscriptionBuyTrialAPICall';
import { IS_MOBILE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { getQueryParams, getUtmSourceString } from 'reactApp/appHelpers/settingsHelpers';
import { BuySubscriptionActions } from 'reactApp/modules/buySubscription/buySubscription.module';
import { BillingBuyTemplates, BuyApiParams, BuyNotifications } from 'reactApp/modules/buySubscription/buySubscription.types';
import { showCardModal, updateCard } from 'reactApp/modules/creditCard/creditCard.saga';
import { CardSelectors } from 'reactApp/modules/creditCard/creditCard.selectors';
import { getFeatureAddCardSkin, getFeatureAddCardSkinTouch } from 'reactApp/modules/features/features.selectors';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { updateSubscriptionsRequest } from 'reactApp/modules/subscriptions/subscriptions.module';
import { renderCancelAutorenewMailSubsDialog } from 'reactApp/sections/QuotaLanding/CancelAutorenewDialog/CancelAutorenewDialog.helpers';
import { channel } from 'redux-saga';
import { call, cancel, put, select, take, takeEvery } from 'redux-saga/effects';

function* openCancelAutorenewMailSubsDialog(action: ReturnType<typeof BuySubscriptionActions.openCancelAutorenewMailSubsDialog>) {
    const { subscription } = action.payload;

    const cancelChannel = channel();

    yield renderCancelAutorenewMailSubsDialog({
        onSuccess: () => cancelChannel.put(true),
        onClose: () => cancelChannel.close(),
        subscription,
    });

    yield take(cancelChannel);

    yield closePopupHelper(popupNames.CANCEL_MAIL_SUBSCRIPTION);

    try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        yield put(BuySubscriptionActions.cancelAutorenew(subscription.id));
        yield put(updateSubscriptionsRequest());
    } catch (error) {
        yield put(
            showSnackbarAction({
                text: 'Не удалось отключить автоплатёж',
                type: SnackbarTypes.failure,
                id: BuyNotifications.cancelAutoRenewFailure,
                closable: true,
            })
        );
        logger.error(error);
        yield cancel();
    }
}

function* addAutoRenew(action: ReturnType<typeof BuySubscriptionActions.addAutorenewToSubscription>) {
    const { subscription } = action.payload;

    const { card } = yield select(CardSelectors.getCard);

    if (!card) {
        yield showCardModal({ isSilent: true });
        yield updateCard({
            showNotifications: true,
        });
    }

    try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        yield put(BuySubscriptionActions.addAutorenew(subscription.id));
        yield put(updateSubscriptionsRequest());
        yield put(
            showSnackbarAction({
                text: 'Автопродление включено',
                type: SnackbarTypes.success,
                id: BuyNotifications.autoRenewSuccess,
                closable: true,
            })
        );
    } catch (error) {
        yield put(
            showSnackbarAction({
                text: 'Не удалось подключить автоплатёж',
                type: SnackbarTypes.failure,
                id: BuyNotifications.autoRenewFailure,
                closable: true,
            })
        );
        logger.error(error);
        yield cancel();
    }
}

function* buyTrial(action: ReturnType<typeof BuySubscriptionActions.buyTrialSubscription>) {
    const { id } = action.payload;
    const { partner } = getQueryParams();
    const skinDesktop = yield select(getFeatureAddCardSkin);
    const skinTouch = yield select(getFeatureAddCardSkinTouch);
    const skin = (IS_MOBILE_BROWSER ? skinTouch : skinDesktop) || BillingBuyTemplates.CHECKOUT_VKPAY;
    const api = new SubscriptionBuyTrialAPICall();

    yield put(BuySubscriptionActions.startLoading());

    const params: Omit<BuyApiParams, 'admitad_uid'> = {
        skin,
        product_id: id,
        source: getUtmSourceString(),
    };

    if (partner) {
        params.partner = partner;
    }

    try {
        const { data } = yield call(api.makeRequest, params);
        yield put(BuySubscriptionActions.setLink({ link: data.url }));
    } catch (error) {
        yield put(BuySubscriptionActions.hasError());
        logger.error(error);
        captureException(error);
        yield cancel();
    }
}

export function* watchBuySubscription() {
    yield takeEvery(BuySubscriptionActions.openCancelAutorenewMailSubsDialog.toString(), openCancelAutorenewMailSubsDialog);
    yield takeEvery(BuySubscriptionActions.addAutorenewToSubscription.toString(), addAutoRenew);
    yield takeEvery(BuySubscriptionActions.buyTrialSubscription.toString(), buyTrial);
}
